import { useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  useFlags,
  useCreateFlag,
  usePatchFlag,
  useFlagOverrides,
  usePutOverride,
  useDeleteOverride,
  RULE_ATTRIBUTES,
  type Flag,
  type Rule,
  type RuleAttribute,
} from "@/api/admin/flags";
import { AdminErrorState } from "@/components/admin/AdminErrorState";
import { ConfirmModal } from "@/components/admin/ConfirmModal";

/**
 * DUE-180 — admin Flags tab: list + create feature flags, edit their
 * segment `rules`, and manage per-user overrides.
 *
 * Toggling a flag's global `enabled` off (it's already on) goes through
 * `ConfirmModal` first — that's a blast-radius change (every user without
 * a matching rule/override loses the feature immediately). Turning one on
 * is comparatively low-risk and applies right away.
 */

const PLATFORMS = ["telegram", "ios", "web"] as const;
const TIERS = ["free", "monthly", "annual"] as const;
const LOCALES = ["ru", "en", "es", "pt"] as const;
const ATTRIBUTE_OPTIONS: Record<RuleAttribute, readonly string[]> = {
  platform: PLATFORMS,
  subscription_tier: TIERS,
  locale: LOCALES,
};

const PAGE: CSSProperties = { padding: "16px 24px 96px", display: "flex", flexDirection: "column", gap: 16 };
const HEAD: CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center" };
const H1: CSSProperties = { fontFamily: "var(--font-display)", fontSize: 22 };
const STATUS: CSSProperties = { fontSize: 12, color: "var(--d-fg-muted)" };
const ROW: CSSProperties = {
  padding: 12,
  border: "1px solid var(--d-line)",
  borderRadius: 12,
  background: "var(--d-ink-2)",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};
const ROW_HEAD: CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" };
const KEY_STYLE: CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--d-fg)" };
const DESC: CSSProperties = { fontFamily: "var(--font-body)", fontSize: 12, color: "var(--d-fg-muted)" };
const SECTION_TITLE: CSSProperties = { fontFamily: "var(--font-body)", fontSize: 13, color: "var(--d-fg)", fontWeight: 600 };
const BTN: CSSProperties = { padding: "6px 12px", borderRadius: 999, border: "none", background: "var(--d-violet)", color: "#fff", cursor: "pointer" };
const BTN_DISABLED: CSSProperties = { ...BTN, background: "var(--d-line)", cursor: "not-allowed" };
const BTN_GHOST: CSSProperties = { padding: "6px 12px", borderRadius: 999, background: "transparent", border: "1px solid var(--d-line)", color: "var(--d-fg)", cursor: "pointer" };
const LINK_BTN: CSSProperties = { background: "none", border: "none", color: "var(--d-violet)", cursor: "pointer", fontFamily: "var(--font-body)", fontSize: 12, padding: 0 };
const FIELD: CSSProperties = { padding: "8px 12px", borderRadius: 12, border: "1px solid var(--d-line)", background: "var(--d-ink)", color: "var(--d-fg)", fontFamily: "var(--font-mono)" };
const OVERLAY: CSSProperties = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 };
const SHEET: CSSProperties = { background: "var(--d-ink-2)", borderRadius: 22, padding: 24, width: "calc(100% - 48px)", maxWidth: 480, display: "flex", flexDirection: "column", gap: 12 };
const CHECK_LABEL: CSSProperties = { display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--d-fg)", fontFamily: "var(--font-body)" };
const RULE_ROW: CSSProperties = { display: "flex", flexDirection: "column", gap: 8, padding: 10, borderRadius: 10, border: "1px solid var(--d-line)" };
const OVERRIDE_ROW: CSSProperties = { display: "flex", alignItems: "center", gap: 12, fontSize: 12 };

export function FlagsPage(): JSX.Element {
  const { t } = useTranslation("admin");
  const { data, isLoading, isError, refetch } = useFlags();
  const create = useCreateFlag();
  const patch = usePatchFlag();
  const [creating, setCreating] = useState(false);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [pendingDisable, setPendingDisable] = useState<Flag | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  function toggleGlobal(flag: Flag): void {
    if (flag.enabled) {
      setPendingDisable(flag);
    } else {
      patch.mutate({ key: flag.key, enabled: true });
    }
  }

  if (isError) return <AdminErrorState onRetry={() => void refetch()} />;

  return (
    <div style={PAGE}>
      <div style={HEAD}>
        <h2 style={H1}>{t("flags.title")}</h2>
        <button style={BTN} onClick={() => setCreating(true)}>
          {t("flags.create")}
        </button>
      </div>
      {status && (
        <div role="status" style={STATUS}>
          {status}
        </div>
      )}
      {isLoading && <div>{t("common.loading")}</div>}
      {!isLoading && (data ?? []).length === 0 && <div style={DESC}>{t("flags.no_flags")}</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {(data ?? []).map((flag) => (
          <FlagRow
            key={flag.key}
            flag={flag}
            expanded={expandedKey === flag.key}
            onToggleExpand={() => setExpandedKey(expandedKey === flag.key ? null : flag.key)}
            onToggleGlobal={() => toggleGlobal(flag)}
          />
        ))}
      </div>

      {creating && (
        <CreateFlagForm
          submitting={create.isPending}
          onClose={() => setCreating(false)}
          onSubmit={(body) =>
            create.mutate(body, {
              onSuccess: () => {
                setStatus(`${body.key} ✓`);
                setCreating(false);
              },
              onError: (err) => setStatus((err as Error).message),
            })
          }
        />
      )}

      <ConfirmModal
        open={pendingDisable !== null}
        title={t("flags.confirm_disable_title")}
        body={t("flags.confirm_disable", { key: pendingDisable?.key ?? "" })}
        destructive
        onConfirm={() => {
          if (pendingDisable) patch.mutate({ key: pendingDisable.key, enabled: false });
          setPendingDisable(null);
        }}
        onClose={() => setPendingDisable(null)}
      />
    </div>
  );
}

function FlagRow({
  flag,
  expanded,
  onToggleExpand,
  onToggleGlobal,
}: {
  flag: Flag;
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleGlobal: () => void;
}): JSX.Element {
  const { t } = useTranslation("admin");
  return (
    <div style={ROW}>
      <div style={ROW_HEAD}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={KEY_STYLE}>{flag.key}</span>
          {flag.description && <span style={DESC}>{flag.description}</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={DESC}>{t("flags.overrides_count", { count: flag.overrides_count })}</span>
          <label style={CHECK_LABEL}>
            <span style={DESC}>{t("flags.global")}</span>
            <input
              aria-label={`${flag.key} ${t("flags.global")}`}
              type="checkbox"
              checked={flag.enabled}
              onChange={onToggleGlobal}
            />
          </label>
          <button style={LINK_BTN} onClick={onToggleExpand}>
            {expanded ? t("flags.collapse") : t("flags.expand")}
          </button>
        </div>
      </div>
      {expanded && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8, borderTop: "1px solid var(--d-line)" }}>
          <RuleEditor flag={flag} />
          <OverridesPanel flagKey={flag.key} />
        </div>
      )}
    </div>
  );
}

function emptyRule(): Rule {
  return { attribute: "platform", values: [], enabled: true };
}

function RuleEditor({ flag }: { flag: Flag }): JSX.Element {
  const { t } = useTranslation("admin");
  const patch = usePatchFlag();
  const [rules, setRules] = useState<Rule[]>(flag.rules);
  const [error, setError] = useState<string | null>(null);
  const dirty = JSON.stringify(rules) !== JSON.stringify(flag.rules);
  // Backend rejects a rule with no selected `values` (RuleIn.values has
  // min_length=1) — block Save client-side instead of round-tripping a
  // 422, since "+ Add rule" starts every new row with an empty selection.
  const hasEmptyValues = rules.some((r) => r.values.length === 0);
  const canSave = dirty && !hasEmptyValues;

  function updateRule(i: number, fragment: Partial<Rule>): void {
    setRules((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...fragment } : r)));
  }
  function toggleValue(i: number, value: string): void {
    setRules((rs) =>
      rs.map((r, idx) => {
        if (idx !== i) return r;
        const values = r.values.includes(value)
          ? r.values.filter((v) => v !== value)
          : [...r.values, value];
        return { ...r, values };
      }),
    );
  }
  function removeRule(i: number): void {
    setRules((rs) => rs.filter((_, idx) => idx !== i));
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={SECTION_TITLE}>{t("flags.rules")}</div>
      {rules.map((rule, i) => (
        // Rules have no stable id in this draft list — index-as-key is fine here.
        <div key={i} style={RULE_ROW}>
          <select
            aria-label={`rule-${i}-attribute`}
            value={rule.attribute}
            onChange={(e) => updateRule(i, { attribute: e.target.value as RuleAttribute, values: [] })}
          >
            {RULE_ATTRIBUTES.map((attr) => (
              <option key={attr} value={attr}>
                {t(`flags.attribute.${attr}`)}
              </option>
            ))}
          </select>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {ATTRIBUTE_OPTIONS[rule.attribute].map((opt) => (
              <label key={opt} style={CHECK_LABEL}>
                <input
                  type="checkbox"
                  aria-label={`rule-${i}-${opt}`}
                  checked={rule.values.includes(opt)}
                  onChange={() => toggleValue(i, opt)}
                />
                {opt}
              </label>
            ))}
          </div>
          <label style={CHECK_LABEL}>
            <input
              type="checkbox"
              aria-label={`rule-${i}-enabled`}
              checked={rule.enabled}
              onChange={(e) => updateRule(i, { enabled: e.target.checked })}
            />
            {t("flags.rule_enabled")}
          </label>
          <div>
            <button style={LINK_BTN} onClick={() => removeRule(i)}>
              {t("common.remove")}
            </button>
          </div>
        </div>
      ))}
      <div style={{ display: "flex", gap: 8 }}>
        <button style={BTN_GHOST} onClick={() => setRules((rs) => [...rs, emptyRule()])}>
          {t("flags.add_rule")}
        </button>
        <button
          style={canSave ? BTN : BTN_DISABLED}
          disabled={!canSave}
          onClick={() =>
            patch.mutate(
              { key: flag.key, rules },
              {
                onSuccess: () => setError(null),
                onError: (err) => setError((err as Error).message),
              },
            )
          }
        >
          {t("common.save")}
        </button>
      </div>
      {hasEmptyValues && <div style={DESC}>{t("flags.rule_values_required")}</div>}
      {error && <div style={{ color: "var(--d-ember)", fontSize: 12 }}>{error}</div>}
    </div>
  );
}

function OverridesPanel({ flagKey }: { flagKey: string }): JSX.Element {
  const { t } = useTranslation("admin");
  const { data, isLoading } = useFlagOverrides(flagKey);
  const put = usePutOverride(flagKey);
  const del = useDeleteOverride(flagKey);
  const [userId, setUserId] = useState("");
  const [error, setError] = useState<string | null>(null);

  function addOverride(): void {
    const parsed = Number(userId);
    if (!userId || Number.isNaN(parsed)) {
      setError(t("flags.override_invalid_user_id"));
      return;
    }
    put.mutate(
      { user_id: parsed, enabled: true },
      {
        onSuccess: () => {
          setUserId("");
          setError(null);
        },
        onError: (err) => setError((err as Error).message),
      },
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={SECTION_TITLE}>{t("flags.overrides")}</div>
      {isLoading && <div>{t("common.loading")}</div>}
      {(data ?? []).map((row) => (
        <div key={row.user_id} style={OVERRIDE_ROW}>
          <span style={DESC}>{row.tg_id ?? "—"}</span>
          <span style={DESC}>{row.username ?? "—"}</span>
          <label style={CHECK_LABEL}>
            <input
              type="checkbox"
              aria-label={`override-${row.user_id}-enabled`}
              checked={row.enabled}
              onChange={(e) => put.mutate({ user_id: row.user_id, enabled: e.target.checked })}
            />
          </label>
          <button style={LINK_BTN} onClick={() => del.mutate(row.user_id)}>
            {t("common.remove")}
          </button>
        </div>
      ))}
      <div style={{ display: "flex", gap: 8 }}>
        <input
          style={FIELD}
          placeholder={t("flags.user_id_placeholder")}
          value={userId}
          onChange={(e) => setUserId(e.target.value.replace(/\D/g, ""))}
        />
        <button style={BTN} onClick={addOverride}>
          {t("flags.add_override")}
        </button>
      </div>
      {error && <div style={{ color: "var(--d-ember)", fontSize: 12 }}>{error}</div>}
    </div>
  );
}

function CreateFlagForm({
  onClose,
  onSubmit,
  submitting,
}: {
  onClose: () => void;
  onSubmit: (body: { key: string; description?: string }) => void;
  submitting: boolean;
}): JSX.Element {
  const { t } = useTranslation("admin");
  const [key, setKey] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  function submit(): void {
    if (!/^[a-z0-9_]{1,64}$/.test(key)) {
      setError(t("flags.key_invalid"));
      return;
    }
    onSubmit({ key, description: description || undefined });
  }

  return (
    <div style={OVERLAY} onClick={onClose}>
      <div style={SHEET} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontFamily: "var(--font-display)" }}>{t("flags.create")}</h3>
        <input
          style={FIELD}
          placeholder={t("flags.key")}
          value={key}
          onChange={(e) => setKey(e.target.value)}
        />
        <input
          style={FIELD}
          placeholder={t("flags.description")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        {error && <div style={{ color: "var(--d-ember)", fontSize: 12 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button style={BTN_GHOST} onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button disabled={submitting} style={submitting ? BTN_DISABLED : BTN} onClick={submit}>
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
